import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { MM } from "@pdfking/quantity"
import type { ColorMap, Page, PageShape } from "@pdfking/renderer-api"
import { ContentRenderer } from "@pdfking/templates"
import { RecordMerge } from "@pdfking/tfunc"

import { COATING_COLORS } from "#constants"
import type { CalendarDesign, ContentLabels, Measure } from "#internal"
import type { ContentRequest } from "#public"

import { AgendaDesigns } from "../agenda/designs/AgendaDesigns"
import { DrawAgendaPage } from "../agenda/DrawAgendaPage"
import { Akindez } from "../calendar/designs/Akindez"
import { Akura } from "../calendar/designs/Akura"
import { Albertus } from "../calendar/designs/Albertus"
import { Belwe } from "../calendar/designs/Belwe"
import { Clan } from "../calendar/designs/Clan"
import { Counterfoil } from "../calendar/designs/Counterfoil"
import { Dash } from "../calendar/designs/Dash"
import { Dubiel } from "../calendar/designs/Dubiel"
import { Eaves } from "../calendar/designs/Eaves"
import { GenericDesigns } from "../calendar/designs/GenericDesigns"
import { Hamurz } from "../calendar/designs/Hamurz"
import { Hooge } from "../calendar/designs/Hooge"
import { Interstate } from "../calendar/designs/Interstate"
import { Kaushan } from "../calendar/designs/Kaushan"
import { Kebby } from "../calendar/designs/Kebby"
import { Lato } from "../calendar/designs/Lato"
import { Lexicon } from "../calendar/designs/Lexicon"
import { Lubalin } from "../calendar/designs/Lubalin"
import { Madison } from "../calendar/designs/Madison"
import { Milo } from "../calendar/designs/Milo"
import { Nelson } from "../calendar/designs/Nelson"
import { Raleway } from "../calendar/designs/Raleway"
import { Roboto } from "../calendar/designs/Roboto"
import { Roddenberry } from "../calendar/designs/Roddenberry"
import { Stymie } from "../calendar/designs/Stymie"
import { Trixie } from "../calendar/designs/Trixie"
import { Vinnie } from "../calendar/designs/Vinnie"
import { Wonderwall } from "../calendar/designs/Wonderwall"
import { DrawCalendarPage } from "../calendar/DrawCalendarPage"
import { InkPalette } from "../colors/InkPalette"
import { BookContent, BookContentOf } from "../compose/BookContent"
import { BoxContent } from "../compose/BoxContent"
import { CalendarContentOf } from "../compose/CalendarContent"
import { CreateComposition } from "../compose/Composition"
import { DepliantContent } from "../compose/DepliantContent"
import { DrawNotepadPage } from "../notepad/DrawNotepadPage"
import { NotebookBackground } from "../types/NotebookBackground"
import { DrawCoating } from "./DrawCoating"

// A book's cover round pages filled with `background`, `pitch` apart, its
// lines from edge to edge when `edge_to_edge`
const notepad = (background: NotebookBackground, pitch = MM(5), edge_to_edge = false) =>
  BookContentOf(page => DrawNotepadPage({ background, edge_to_edge, footer: true, page, pitch }))

// A calendar of `design`, its pages drawn whole, its cover the calendar's
// (see CalendarContentOf) — or in the book's covers, as a pad's base is
const calendar = (design: CalendarDesign, book_covers = false) =>
  book_covers
    ? BookContentOf(DrawCalendarPage(design), true)
    : CalendarContentOf(DrawCalendarPage(design))

const RENDERERS: Record<ContentRenderer, typeof BookContent> = {
  [ContentRenderer.BOOK]: BookContent,
  [ContentRenderer.BOX]: BoxContent,
  [ContentRenderer.DEPLIANT]: DepliantContent,
  [ContentRenderer.NOTEPAD_BLANK]: notepad(NotebookBackground.BLANK),
  [ContentRenderer.NOTEPAD_LINED]: notepad(NotebookBackground.LINES, MM(10.25), true),
  [ContentRenderer.NOTEPAD_SQUARED]: notepad(NotebookBackground.SQUARES),
  [ContentRenderer.NOTEPAD_DOTTED]: notepad(NotebookBackground.DOTS),
  [ContentRenderer.NOTEPAD_LINED_8]: notepad(NotebookBackground.LINES, MM(8)),
  [ContentRenderer.NOTEPAD_SQUARED_10]: notepad(NotebookBackground.SQUARES, MM(10)),
  [ContentRenderer.NOTEPAD_MILLIMETER]: notepad(NotebookBackground.MILLIMETER, MM(1)),
  [ContentRenderer.CALENDAR_MONTH]: calendar(GenericDesigns.month),
  [ContentRenderer.CALENDAR_SPREAD]: calendar(GenericDesigns.spread),
  [ContentRenderer.CALENDAR_YEAR]: calendar(GenericDesigns.year),
  [ContentRenderer.CALENDAR_AKINDEZ]: calendar(Akindez),
  [ContentRenderer.CALENDAR_AKURA]: calendar(Akura),
  [ContentRenderer.CALENDAR_BELWE]: calendar(Belwe),
  [ContentRenderer.CALENDAR_CLAN]: calendar(Clan),
  [ContentRenderer.CALENDAR_DASH]: calendar(Dash),
  [ContentRenderer.CALENDAR_DUBIEL]: calendar(Dubiel),
  [ContentRenderer.CALENDAR_EAVES]: calendar(Eaves),
  [ContentRenderer.CALENDAR_HAMURZ]: calendar(Hamurz),
  [ContentRenderer.CALENDAR_HOOGE]: calendar(Hooge),
  [ContentRenderer.CALENDAR_INTERSTATE]: calendar(Interstate),
  [ContentRenderer.CALENDAR_KAUSHAN]: calendar(Kaushan),
  [ContentRenderer.CALENDAR_KEBBY]: calendar(Kebby),
  [ContentRenderer.CALENDAR_LATO]: calendar(Lato),
  [ContentRenderer.CALENDAR_MADISON]: calendar(Madison),
  [ContentRenderer.CALENDAR_MILO]: calendar(Milo),
  [ContentRenderer.CALENDAR_NELSON]: calendar(Nelson),
  [ContentRenderer.CALENDAR_RALEWAY]: calendar(Raleway),
  [ContentRenderer.CALENDAR_ROBOTO]: calendar(Roboto),
  [ContentRenderer.CALENDAR_RODDENBERRY]: calendar(Roddenberry),
  [ContentRenderer.CALENDAR_STYMIE]: calendar(Stymie),
  [ContentRenderer.CALENDAR_TRIXIE]: calendar(Trixie),
  [ContentRenderer.CALENDAR_VINNIE]: calendar(Vinnie),
  [ContentRenderer.CALENDAR_WONDERWALL]: calendar(Wonderwall),
  [ContentRenderer.AGENDA_WEEKLY]: BookContentOf(DrawAgendaPage(AgendaDesigns.weekly)),
  [ContentRenderer.AGENDA_DAILY]: BookContentOf(DrawAgendaPage(AgendaDesigns.daily)),
  [ContentRenderer.AGENDA_ALTAIR]: BookContentOf(DrawAgendaPage(AgendaDesigns.altair)),
  [ContentRenderer.AGENDA_KIONA]: BookContentOf(DrawAgendaPage(AgendaDesigns.kiona)),
  [ContentRenderer.AGENDA_KIONA_DAILY]: BookContentOf(DrawAgendaPage(AgendaDesigns.kiona_daily)),
  [ContentRenderer.AGENDA_LEIXO]: BookContentOf(DrawAgendaPage(AgendaDesigns.leixo)),
  [ContentRenderer.AGENDA_MUNA]: BookContentOf(DrawAgendaPage(AgendaDesigns.muna)),
  [ContentRenderer.AGENDA_PAPYRUS]: BookContentOf(DrawAgendaPage(AgendaDesigns.papyrus)),
  [ContentRenderer.AGENDA_PERPETUAL]: BookContentOf(DrawAgendaPage(AgendaDesigns.perpetual)),
  [ContentRenderer.AGENDA_PERPETUAL_DAILY]: BookContentOf(
    DrawAgendaPage(AgendaDesigns.perpetual_daily)
  ),
  [ContentRenderer.PLANNER_WEEKLY]: calendar(Albertus),
  [ContentRenderer.PLANNER_PERPETUAL]: calendar(Lubalin),
  [ContentRenderer.PLANNER_ALBERTUS]: calendar(Albertus),
  [ContentRenderer.PLANNER_LEXICON]: calendar(Lexicon),
  [ContentRenderer.PLANNER_LUBALIN]: calendar(Lubalin),
  [ContentRenderer.COUNTERFOIL]: calendar(Counterfoil, true),
}

// The page's shapes: the coating's film on that layer, whatever the page; on
// any other the renderer's pieces, in its own colors on the print, the spot's
// alone on an overlay
const shapes = (measure: Measure, labels: ContentLabels, request: ContentRequest): PageShape[] =>
  request.layer === "coating"
    ? DrawCoating({ bleed: request.bleed, size: request.size })
    : RENDERERS[request.renderer](CreateComposition(measure, labels, request))

/** A generated page and the colors it plots with. */
export type ContentPage = { color_map: ColorMap; page: Page }

/** The page `request` asks for, as shapes in its own units, y up from its
 * corner: text measured by `measure`, its fixed words `labels`, on the plate
 * of its layer. */
export const GenerateContent = ({
  labels,
  measure,
  request,
}: {
  labels: ContentLabels
  measure: Measure
  request: ContentRequest
}): ContentPage => ({
  color_map: RecordMerge(InkPalette(request.palette, request.layer), COATING_COLORS),
  page: {
    ...request.size,
    bleed: request.bleed,
    shapes: shapes(measure, labels, request),
    x: ZERO_DISTANCE,
    y: ZERO_DISTANCE,
  },
})
